import { useEffect, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { useLanguage } from '@context/LanguageContext'
import { useDeviceType } from '@hooks/useDeviceType'
import { useFocusTrap } from '@hooks/useFocusTrap'
import { MemoramaIcon, GamesIcon, CloseIcon } from '../Icons'
import styles from './GameSelector.module.css'
import baseStyles from '@styles/modalBase.module.css'

export default function GameSelector ({ isOpen, onClose, triggerRef }) {
  const { tFunc, lang } = useLanguage()
  const { isMobile, isTabletVertical } = useDeviceType()
  const navigate = useNavigate()
  const modalRef = useRef(null)
  const titleRef = useRef(null)

  const isCompactMode = isMobile || isTabletVertical

  useFocusTrap(modalRef)

  // Game options
  const games = [
    {
      id: 'memorama',
      icon: MemoramaIcon,
      label: tFunc('core.navigation.memorama'),
      path: `/${lang}/memorama`
    },
    {
      id: 'puzzle',
      icon: GamesIcon,
      label: tFunc('core.navigation.puzzle'),
      path: `/${lang}/puzzle`
    }
  ]

  const handleGameSelect = (path) => {
    navigate(path)
    onClose()
  }

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape' && isOpen) {
        onClose()
      }
    }

    if (isOpen) {
      window.addEventListener('keydown', handleKeyDown)
      return () => window.removeEventListener('keydown', handleKeyDown)
    }
  }, [isOpen, onClose])

  // Close on click outside (desktop mode only)
  useEffect(() => {
    if (!isOpen || isCompactMode) return

    const handleClickOutside = (e) => {
      if (
        modalRef.current &&
        !modalRef.current.contains(e.target) &&
        triggerRef?.current &&
        !triggerRef.current.contains(e.target)
      ) {
        onClose()
      }
    }

    // Small delay to avoid immediate closure on trigger click
    const timeoutId = setTimeout(() => {
      document.addEventListener('mousedown', handleClickOutside)
    }, 0)

    return () => {
      clearTimeout(timeoutId)
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [isOpen, isCompactMode, onClose, triggerRef])

  // Focus management
  useEffect(() => {
    if (isOpen && titleRef.current) {
      titleRef.current.focus()
    }
  }, [isOpen])

  if (!isOpen) return null

  return (
    <div
      className={baseStyles.overlayBottom}
      role='dialog'
      aria-modal='true'
      aria-labelledby='game-selector-title'
    >
      <div className={styles.gameSelector} ref={modalRef}>
        {/* Handle (mobile only) */}
        {isCompactMode && <div className={baseStyles.bottomHandle} />}

        {/* Header */}
        <header className={styles.header}>
          <h2
            id='game-selector-title'
            className={styles.title}
            ref={titleRef}
            tabIndex='-1'
          >
            {tFunc('core.navigation.selectGame')}
          </h2>
          <button
            onClick={onClose}
            className={styles.close}
            aria-label={tFunc('core.access.menu.close')}
            title={tFunc('core.access.menu.close')}
          >
            <CloseIcon size={20} stroke={1.5} aria-hidden='true' />
            <span className='sr-only'>{tFunc('core.access.menu.close')}</span>
          </button>
        </header>

        {/* Content */}
        <div className={styles.content}>
          {games.map(({ id, icon: Icon, label, path }) => (
            <div key={id} className={styles.item}>
              <div className={styles.itemHeader}>
                <Icon size={18} stroke={1.5} aria-hidden='true' />
                <span className={styles.itemLabel}>{label}</span>
              </div>
              <button
                onClick={() => handleGameSelect(path)}
                className={styles.itemBtn}
                aria-label={`${tFunc('core.navigation.goTo')} ${label}`}
              >
                {tFunc('core.navigation.select')}
              </button>
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}
